import useStore from '../store/useStore';

/** The current company's currency. */
export const currencyCode = () => useStore.getState().currentCompany?.currency || 'GBP';

const moneyFormat = (options) => new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: currencyCode(),
  currencyDisplay: 'narrowSymbol',
  ...options,
});

/** The current company's currency symbol, such as £. */
export const currencySymbol = () => moneyFormat({ maximumFractionDigits: 0 }).formatToParts(0).find((p) => p.type === 'currency')?.value || '';

/** Money in the current company's currency, with no decimals. */
export const formatWholeMoney = (n) => moneyFormat({ maximumFractionDigits: 0 }).format(n);

/**
 * Display a measure value by its format: percent measures hold fractions
 * (0.312 shows as 31.2%), currency uses the company's currency, other numbers
 * are grouped with up to two decimals. Empty values show as a dash.
 */
export function formatMeasure(value, format) {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return String(value);
  switch (format) {
    case 'percent': {
      const pct = Math.round(n * 1000) / 10;
      return `${pct.toLocaleString(undefined, { maximumFractionDigits: Number.isInteger(pct) ? 0 : 1 })}%`;
    }
    case 'currency':
      return moneyFormat({ minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 }).format(n);
    default:
      return n.toLocaleString(undefined, { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 });
  }
}

/** A change between two values of a measure: percentage points for percent measures. */
export function formatChange(diff, format) {
  if (format === 'percent') {
    const pts = Math.round(Math.abs(diff) * 1000) / 10;
    return `${pts.toLocaleString(undefined, { maximumFractionDigits: 1 })} pts`;
  }
  return formatMeasure(Math.abs(diff), format);
}

/** Axis ticks: shorter than cells (no decimals for large numbers). */
export function formatTick(value, format) {
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  // One decimal place where it is needed, so small rates such as 0.5% and 1.5% do not repeat as 1% and 2%.
  if (format === 'percent') return `${Number((n * 100).toFixed(1))}%`;
  if (format === 'currency') return formatWholeMoney(n);
  return n.toLocaleString(undefined, { maximumFractionDigits: Math.abs(n) >= 100 ? 0 : 2 });
}
